/**
 * ✨ Glimmer Sanctum — purchase, level, equip up to 3 Glimmers per battle.
 *
 * Tabs:
 *   • Collection — all 7 Glimmers w/ progress bars + buy/feed actions
 *   • Loadout    — pick the 3 you ride into battle
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { confirmDialog } from "@/components/ui/confirm-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GlimmerSanctum() {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState(null);
  const [tab, setTab] = useState("collection");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/glimmers/state`);
      setState(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sanctum offline.");
    }
  }, []);

  useEffect(() => { if (authLoading || !user) return; refresh(); }, [authLoading, user, refresh]);

  if (authLoading) return null;
  if (!user) return (
    <div className="min-h-screen bg-[#0A0A0A] text-neutral-200">
      <Header />
      <div className="max-w-3xl mx-auto p-8 text-center text-neutral-400">🔒 Sign in to enter the Glimmer Sanctum.</div>
      <Footer />
    </div>
  );
  if (!state) return (
    <div className="min-h-screen bg-[#0A0A0A] text-neutral-200">
      <Header />
      <div className="max-w-3xl mx-auto p-8 text-center text-neutral-500">The Sanctum hums…</div>
      <Footer />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-neutral-200" data-testid="glimmer-sanctum">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-6 pb-24">
        <div className="mb-6 rounded-2xl border border-fuchsia-500/30 bg-gradient-to-br from-[#1a0820] via-[#0A0A0A] to-[#0820] p-5 shadow-[0_0_30px_rgba(217,70,239,0.15)]">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-3xl font-black tracking-wide text-fuchsia-300" style={{ fontFamily: 'Anton, sans-serif' }}>
                ✨ GLIMMER SANCTUM
              </div>
              <div className="text-xs text-neutral-400 mt-1">
                Max 3 Glimmers per battle · Unlocks at Pet Lv {state.unlock_pet_level} · 🔹 Pieces level your kit
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Pill icon="Ⓞ"  label="OD"      value={state.omni_dollars}  testid="gl-wallet-od" />
              <Pill icon="🔹" label="Pieces"  value={state.glimmer_pieces} testid="gl-wallet-pieces" />
              <Pill icon="🐦" label="Pet Lv"  value={state.pet_level}      testid="gl-pet-lv" />
            </div>
          </div>
          {!state.unlocked && (
            <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-950/20 px-3 py-2 text-xs text-amber-200" data-testid="gl-locked-banner">
              🔒 Glimmers unlock at Pet Lv {state.unlock_pet_level}. You're at Lv {state.pet_level}. Keep grinding.
            </div>
          )}
        </div>

        <div className="flex gap-2 mb-5 border-b border-neutral-800 overflow-x-auto">
          <TabBtn label="✨ Collection" active={tab === "collection"} onClick={() => setTab("collection")} testid="gl-tab-collection" />
          <TabBtn label={`🎯 Loadout (${state.loadout.length}/${state.max_loadout})`} active={tab === "loadout"} onClick={() => setTab("loadout")} testid="gl-tab-loadout" />
        </div>

        {tab === "collection" && <CollectionTab state={state} busy={busy} setBusy={setBusy} refresh={refresh} />}
        {tab === "loadout"    && <LoadoutTab    state={state} busy={busy} setBusy={setBusy} refresh={refresh} />}
      </div>
      <Footer />
    </div>
  );
}

function Pill({ icon, label, value, testid }) {
  return (
    <div className="px-3 py-1.5 rounded-full bg-black/40 border border-neutral-700 text-sm flex items-center gap-1.5" data-testid={testid}>
      <span>{icon}</span>
      <span className="font-bold text-fuchsia-300">{value}</span>
      <span className="text-neutral-500 text-xs">{label}</span>
    </div>
  );
}

function TabBtn({ label, active, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`px-4 py-2 text-sm font-bold border-b-2 transition whitespace-nowrap ${
        active ? "border-fuchsia-400 text-fuchsia-200" : "border-transparent text-neutral-500 hover:text-neutral-300"
      }`}>{label}</button>
  );
}

/* ───────── Collection ───────── */
function CollectionTab({ state, busy, setBusy, refresh }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="gl-collection">
      {state.glimmers.map(g => (
        <GlimmerCard key={g.id} g={g} state={state} busy={busy} setBusy={setBusy} refresh={refresh} />
      ))}
    </div>
  );
}

function GlimmerCard({ g, state, busy, setBusy, refresh }) {
  const [feedQty, setFeedQty] = useState(1);
  const xpPct = g.owned && g.xp_to_next > 0 ? Math.min(100, Math.round((g.xp / g.xp_to_next) * 100)) : (g.owned ? 100 : 0);
  const onBuy = async () => {
    if (busy) return;
    const ok = await confirmDialog({
      title: `Unlock Glimmer: ${g.name}?`,
      message: `${g.icon} ${g.name}
Cost: Ⓞ ${g.price_od} OD (permanent unlock, feed with 🔹 Pieces to level up).`,
      confirmLabel: `Buy — ${g.price_od} OD`,
      cancelLabel: "Not yet",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/glimmers/buy`, { glimmer_id: g.id });
      toast.success(`${g.icon} ${g.name} acquired! (-${r.data.spent} Ⓞ)`, { duration: 5000 });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Buy failed.");
    } finally { setBusy(false); }
  };
  const onFeed = async () => {
    if (busy) return;
    const qty = Math.max(1, Math.min(state.glimmer_pieces, parseInt(feedQty) || 1));
    setBusy(true);
    try {
      const r = await axios.post(`${API}/glimmers/feed`, { glimmer_id: g.id, qty });
      const dings = r.data.dings || [];
      if (dings.length > 0) {
        toast.success(`${g.icon} ${g.name} → Lv ${r.data.after_level}!`, { duration: 7000 });
      } else {
        toast.success(`🔹 Fed ${qty} pieces to ${g.name} (+${qty * state.xp_per_piece} XP)`, { duration: 4500 });
      }
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Feed failed.");
    } finally { setBusy(false); }
  };

  const canBuy = state.unlocked && !g.owned && g.id !== "rage" && state.omni_dollars >= g.price_od;
  const canFeed = state.unlocked && g.owned && state.glimmer_pieces > 0 && g.level < state.max_level;
  const lockoutLabel = (g.hero_lockout || []).map(c => c.charAt(0).toUpperCase() + c.slice(1)).join(", ");

  return (
    <div className={`rounded-xl border-2 p-3 ${
        g.owned
          ? "border-fuchsia-500/50 bg-gradient-to-br from-fuchsia-950/30 to-black/40 shadow-[0_0_22px_rgba(217,70,239,0.2)]"
          : "border-neutral-800 bg-black/30 opacity-90"
      }`} data-testid={`gl-card-${g.id}`}>
      <div className="flex items-start gap-3 mb-2">
        <div className={`text-5xl ${g.owned ? "" : "grayscale opacity-50"}`}>{g.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-black text-fuchsia-200">{g.name}</span>
            {g.owned && <span className="text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-500/20 text-fuchsia-200 font-bold">Lv {g.level}</span>}
            {!g.owned && g.id === "rage" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-200">🔒 Lv 10</span>}
            {!g.owned && g.id !== "rage" && state.unlocked && <span className="text-[10px] px-2 py-0.5 rounded-full bg-neutral-700 text-neutral-300">Ⓞ {g.price_od}</span>}
          </div>
          <div className="text-[10px] text-neutral-500 mt-0.5 leading-tight">{g.description}</div>
          {lockoutLabel && (
            <div className="text-[10px] text-rose-400 mt-1">🔒 Hero lockout: {lockoutLabel}</div>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-1 text-[10px] mb-2">
        <Stat label="Cooldown" value={`${g.cooldown_sec}s`} />
        <Stat label="Uses"     value={`×${g.uses_per_battle}`} />
        <Stat label="Potency"  value={g.potency_bonus_pct > 0 ? `+${g.potency_bonus_pct}%` : "base"} />
      </div>

      {g.owned && (
        <>
          {g.xp_to_next > 0 ? (
            <>
              <div className="h-1.5 rounded-full bg-neutral-900 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-fuchsia-500 to-amber-400 transition-all" style={{ width: `${xpPct}%` }} />
              </div>
              <div className="text-[10px] text-neutral-500 mt-1 flex justify-between">
                <span>{g.xp} / {g.xp_to_next} XP</span>
                <span className="text-fuchsia-300">→ Lv {g.level + 1}</span>
              </div>
            </>
          ) : (
            <div className="text-[10px] text-amber-300 text-center font-bold mt-1">✦ MAX LEVEL</div>
          )}
        </>
      )}

      {/* Actions */}
      <div className="mt-3 flex gap-2">
        {!g.owned ? (
          <button data-testid={`gl-buy-${g.id}`} disabled={!canBuy || busy} onClick={onBuy}
            className="flex-1 px-3 py-2 rounded-lg bg-gradient-to-r from-fuchsia-600 to-purple-600 hover:from-fuchsia-500 hover:to-purple-500 disabled:opacity-50 text-white text-xs font-bold">
            {g.id === "rage" ? "🔒 Free at Pet Lv 10" : !state.unlocked ? "🔒 Locked" : `Ⓞ Buy ${g.price_od}`}
          </button>
        ) : (
          <div className="flex-1 flex gap-1">
            <input type="number" min={1} max={Math.max(1, state.glimmer_pieces)}
              value={feedQty}
              data-testid={`gl-feed-qty-${g.id}`}
              onChange={e => setFeedQty(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-16 px-2 py-2 rounded-lg bg-black/60 border border-neutral-700 text-center text-xs" />
            <button data-testid={`gl-feed-${g.id}`} disabled={!canFeed || busy} onClick={onFeed}
              className="flex-1 px-3 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-red-500 hover:from-amber-400 hover:to-red-400 disabled:opacity-50 text-black text-xs font-black">
              🔹 Feed (+{(parseInt(feedQty) || 1) * state.xp_per_piece} XP)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-neutral-800 bg-black/40 py-1 px-1.5 text-center">
      <div className="text-fuchsia-200 font-bold leading-tight">{value}</div>
      <div className="text-neutral-500 leading-tight">{label}</div>
    </div>
  );
}

/* ───────── Loadout ───────── */
function LoadoutTab({ state, busy, setBusy, refresh }) {
  const [selected, setSelected] = useState(state.loadout);
  const ownedList = state.glimmers.filter(g => g.owned);
  const toggle = (id) => {
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= state.max_loadout) {
        toast.warning(`Max ${state.max_loadout} Glimmers per battle.`);
        return prev;
      }
      return [...prev, id];
    });
  };
  const onSave = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/glimmers/loadout`, { glimmer_ids: selected });
      toast.success(`🎯 Loadout saved — ${selected.length}/${state.max_loadout}`, { duration: 4500 });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  if (ownedList.length === 0) return (
    <div className="text-center py-12 text-neutral-500" data-testid="gl-loadout-empty">
      <div className="text-5xl mb-3">🔒</div>
      No Glimmers owned yet. Hit Pet Lv 10 for free Rage or visit the Sanctum to buy with OD.
    </div>
  );

  return (
    <div data-testid="gl-loadout">
      <div className="text-xs text-neutral-400 mb-3">
        Pick up to <span className="text-fuchsia-300 font-bold">{state.max_loadout}</span> Glimmers to ride into battle.
        Tap to toggle. Hero-class lockouts apply once the Heroes arc ships.
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-4">
        {ownedList.map(g => {
          const on = selected.includes(g.id);
          return (
            <button key={g.id} data-testid={`gl-slot-${g.id}`}
              onClick={() => toggle(g.id)}
              className={`rounded-xl border-2 p-3 text-center transition ${
                on ? "border-fuchsia-400 bg-fuchsia-500/15 shadow-[0_0_18px_rgba(217,70,239,0.4)]"
                   : "border-neutral-800 bg-black/30 hover:border-neutral-600"
              }`}>
              <div className="text-4xl mb-1">{g.icon}</div>
              <div className="font-bold text-xs text-fuchsia-200">{g.name}</div>
              <div className="text-[10px] text-neutral-500 mt-0.5">Lv {g.level} · CD {g.cooldown_sec}s · ×{g.uses_per_battle}</div>
              {on && <div className="mt-1.5 text-[10px] text-fuchsia-300 font-black">✔ EQUIPPED</div>}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-3">
        <button data-testid="gl-loadout-save" disabled={busy} onClick={onSave}
          className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-fuchsia-600 to-purple-600 hover:from-fuchsia-500 hover:to-purple-500 disabled:opacity-50 text-white font-black tracking-wider">
          🎯 SAVE LOADOUT ({selected.length}/{state.max_loadout})
        </button>
        <span className="text-[10px] text-neutral-500">Saved loadout: {state.loadout.length ? state.loadout.join(", ") : "—"}</span>
      </div>
    </div>
  );
}
